import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, User, AlertCircle, Loader2 } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #111827 60%, #0d1a0d 100%)' }}>
      {/* Left: Branding */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_6d55a2c9-77b0-47cd-9fb3-a9a0e5fd0f87/artifacts/50vp5qrw_VALORIZA_color_negativo%206.png"
            alt="Valoriza"
            className="h-12 object-contain"
          />
        </div>
        <div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
            GMAO Ecoparque
            <br />
            <span className="text-[#16a34a]">de Cáceres</span>
          </h1>
          <p className="text-gray-400 text-base leading-relaxed max-w-sm">
            Sistema de Gestión de Mantenimiento Asistido por Ordenador. 
            Control total de maquinaria, órdenes de trabajo y alertas de mantenimiento.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { label: 'Equipos', value: '43+' },
              { label: 'Módulos', value: '8' },
              { label: 'Exportación', value: 'XML' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-2xl font-bold text-[#16a34a]" style={{ fontFamily: 'Barlow, sans-serif' }}>{stat.value}</p>
                <p className="text-xs text-gray-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[11px] text-gray-600">Powered by Valoriza's Innovation Team</p>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_6d55a2c9-77b0-47cd-9fb3-a9a0e5fd0f87/artifacts/50vp5qrw_VALORIZA_color_negativo%206.png"
              alt="Valoriza"
              className="h-10 object-contain mx-auto mb-3"
            />
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Iniciar Sesión</h2>
              <p className="text-sm text-gray-500 mt-1">GMAO · Ecoparque de Cáceres</p>
            </div>

            {error && (
              <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="login-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Usuario</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="admin"
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#16a34a] focus:border-transparent transition-all"
                    data-testid="login-username"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#16a34a] focus:border-transparent transition-all"
                    data-testid="login-password"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                data-testid="login-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </button>
            </form>

            <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 font-medium mb-1">Credenciales de acceso:</p>
              <p className="text-xs text-gray-600">Admin: <span className="font-mono text-gray-800">admin / admin123</span></p>
              <p className="text-xs text-gray-600">Técnico: <span className="font-mono text-gray-800">tecnico / tecnico123</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
